'use client';

import { type CSSProperties, type ReactNode, useEffect, useMemo, useState } from 'react';
import { type JSONContent } from '@tiptap/core';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Pencil, Plus, Trash2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { RUNNER_DISPATCH_POLICY_STATES } from '@agentteams/core-constants';
import { toast } from 'sonner';
import { getAuthSession } from '../lib/authSession';
import { type RunnerDispatchPolicy, type RunnerDispatchPolicyEntityType } from '../lib/api';
import { useProjectRole } from '../hooks/useProjectRole';
import { useConventions } from '../hooks/queries/useConventions';
import {
  useCreateRunnerDispatchPolicyMutation,
  useCreateRunnerDispatchPolicyPresetMutation,
  useDeleteRunnerDispatchPolicyMutation,
  useReorderRunnerDispatchPoliciesMutation,
  useRunnerDispatchPolicies,
  useUpdateRunnerDispatchPolicyPresetMutation,
} from '../hooks/queries/useRunnerDispatchPolicies';
import { prepareUserConventionOptionsForPicker, UserConventionSelector } from './UserConventionSelector';
import DeleteConfirmModal from './DeleteConfirmModal';
import { Select, type SelectOption } from './Select';
import { Spinner } from './loading/Spinner';
import { TiptapEditor } from './TiptapEditor';

const statusesByEntityType = RUNNER_DISPATCH_POLICY_STATES;

const emptyPrompt: JSONContent = { type: 'doc', content: [] };
const emptyPolicies: RunnerDispatchPolicy[] = [];

type RunnerDispatchPolicySettingsViewProps = {
  projectId: string;
};

function SortablePolicyItem({
  policy,
  isDisabled,
  dragLabel,
  children,
}: {
  policy: RunnerDispatchPolicy;
  isDisabled: boolean;
  dragLabel: string;
  children: ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: policy.id,
    disabled: isDisabled,
  });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <li ref={setNodeRef} style={style} className={isDragging ? 'relative z-10 opacity-60 shadow-lg' : undefined}>
      <article className="flex items-center gap-2 rounded-xl border border-base-300 bg-base-100 p-3">
        <button
          type="button"
          className="inline-flex h-6 w-6 shrink-0 cursor-grab items-center justify-center rounded text-[var(--color-fg-muted)] transition-colors hover:bg-base-200 hover:text-base-content active:cursor-grabbing disabled:cursor-not-allowed disabled:opacity-40"
          aria-label={dragLabel}
          disabled={isDisabled}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" aria-hidden="true" />
        </button>
        <div className="min-w-0 flex-1">{children}</div>
      </article>
    </li>
  );
}

export default function RunnerDispatchPolicySettingsView({ projectId }: RunnerDispatchPolicySettingsViewProps) {
  const t = useTranslations('workspace.page');
  const tCommon = useTranslations('common');
  const session = useMemo(() => getAuthSession(), []);
  const token = session?.token ?? null;
  const { canEditConventions, isLoadingRole } = useProjectRole({ projectId });
  const { data: policies = emptyPolicies, isLoading } = useRunnerDispatchPolicies(token, projectId);
  // 정책이 주입하는 컨벤션은 러너 런타임에서 PROJECT scope만 로드·동기화되므로, 선택 목록도
  // PROJECT로 제한한다. (PERSONAL을 담아도 주입 시점에 조용히 누락됨)
  const { data: conventions = [] } = useConventions(token, projectId, { scope: 'PROJECT', pageSize: 100 });
  const createPolicy = useCreateRunnerDispatchPolicyMutation(token, projectId);
  const createPreset = useCreateRunnerDispatchPolicyPresetMutation(token, projectId);
  const updatePreset = useUpdateRunnerDispatchPolicyPresetMutation(token, projectId);
  const deletePolicy = useDeleteRunnerDispatchPolicyMutation(token, projectId);
  const reorderPolicies = useReorderRunnerDispatchPoliciesMutation(token, projectId);

  const [entityType, setEntityType] = useState<RunnerDispatchPolicyEntityType>('PLAN');
  const [entityState, setEntityState] = useState<string>(statusesByEntityType.PLAN[0]);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [prompt, setPrompt] = useState<JSONContent>(emptyPrompt);
  const [planMode, setPlanMode] = useState(false);
  const [userConventionIds, setUserConventionIds] = useState<string[]>([]);
  const [editingPolicyId, setEditingPolicyId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [editingPrompt, setEditingPrompt] = useState<JSONContent>(emptyPrompt);
  const [editingPlanMode, setEditingPlanMode] = useState(false);
  const [editingUserConventionIds, setEditingUserConventionIds] = useState<string[]>([]);
  const [orderedPolicies, setOrderedPolicies] = useState<RunnerDispatchPolicy[]>([]);
  const [disablingPolicy, setDisablingPolicy] = useState<RunnerDispatchPolicy | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    setEntityState(statusesByEntityType[entityType][0]);
  }, [entityType]);

  useEffect(() => {
    setIsCreateOpen(false);
    setEditingPolicyId(null);
  }, [projectId]);

  const entityOptions: SelectOption[] = useMemo(
    () =>
      (Object.keys(statusesByEntityType) as RunnerDispatchPolicyEntityType[]).map((value) => ({
        value,
        label: t(`dispatchPolicies.entityTypes.${value}`),
      })),
    [t],
  );
  const stateOptions: SelectOption[] = useMemo(
    () => statusesByEntityType[entityType].map((value) => ({ value, label: value })),
    [entityType],
  );
  const currentPolicies = useMemo(
    () => policies.filter((policy) => policy.entityType === entityType && policy.entityState === entityState),
    [entityState, entityType, policies],
  );
  useEffect(() => {
    setOrderedPolicies(currentPolicies);
  }, [currentPolicies]);
  const conventionOptions = useMemo(
    () =>
      prepareUserConventionOptionsForPicker(
        conventions.map((convention) => ({
          id: convention.id,
          title: convention.title,
          category: convention.category,
          contentTokenCount: convention.contentTokenCount ?? null,
          runnerInjectedCount: convention.runnerInjectedCount,
          trigger: convention.trigger,
          updatedAt: convention.updatedAt,
        })),
      ),
    [conventions],
  );
  const canEdit = canEditConventions && !isLoadingRole;

  const resetCreateForm = () => {
    setName('');
    setPrompt(emptyPrompt);
    setPlanMode(false);
    setUserConventionIds([]);
  };
  const toggleConvention = (id: string, checked: boolean, isEditing = false) => {
    const setter = isEditing ? setEditingUserConventionIds : setUserConventionIds;
    setter((previous) =>
      checked ? (previous.includes(id) ? previous : [...previous, id]) : previous.filter((value) => value !== id),
    );
  };

  const handleCreate = async () => {
    try {
      const promptPresetId = (
        await createPreset.mutateAsync({
          name: name.trim(),
          prompt,
          planMode,
          userConventionIds,
        })
      ).id;
      await createPolicy.mutateAsync({ entityType, entityState, promptPresetId });
      toast.success(t('dispatchPolicies.created'));
      resetCreateForm();
      setIsCreateOpen(false);
    } catch {
      toast.error(t('dispatchPolicies.saveFailed'));
    }
  };

  const startEditing = (policyId: string) => {
    const policy = currentPolicies.find((item) => item.id === policyId);
    if (!policy) return;
    setEditingPolicyId(policyId);
    setEditingName(policy.promptPreset.name);
    setEditingPrompt(policy.promptPreset.prompt as JSONContent);
    setEditingPlanMode(policy.promptPreset.planMode);
    setEditingUserConventionIds(policy.promptPreset.userConventionIds);
  };

  const saveEditing = async () => {
    const policy = currentPolicies.find((item) => item.id === editingPolicyId);
    if (!policy) return;
    try {
      await updatePreset.mutateAsync({
        id: policy.promptPresetId,
        input: {
          name: editingName.trim(),
          prompt: editingPrompt,
          planMode: editingPlanMode,
          userConventionIds: editingUserConventionIds,
        },
      });
      toast.success(t('dispatchPolicies.updated'));
      setEditingPolicyId(null);
    } catch {
      toast.error(t('dispatchPolicies.saveFailed'));
    }
  };

  const handlePolicyDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = orderedPolicies.findIndex((policy) => policy.id === active.id);
    const newIndex = orderedPolicies.findIndex((policy) => policy.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const nextPolicies = arrayMove(orderedPolicies, oldIndex, newIndex);
    setOrderedPolicies(nextPolicies);
    void reorderPolicies.mutateAsync(nextPolicies.map((policy) => policy.id)).catch(() => {
      setOrderedPolicies(currentPolicies);
      toast.error(t('dispatchPolicies.saveFailed'));
    });
  };

  return (
    <section className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-base-content">{t('dispatchPolicies.title')}</h3>
        <p className="mt-1 text-xs leading-5 text-[var(--color-fg-muted)]">{t('dispatchPolicies.description')}</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="space-y-1 text-xs font-medium text-[var(--color-fg-subtle)]">
          {t('dispatchPolicies.entityType')}
          <Select
            options={entityOptions}
            value={entityType}
            onChange={(value) => setEntityType(value as RunnerDispatchPolicyEntityType)}
            fullWidth
          />
        </label>
        <label className="space-y-1 text-xs font-medium text-[var(--color-fg-subtle)]">
          {t('dispatchPolicies.entityState')}
          <Select options={stateOptions} value={entityState} onChange={setEntityState} fullWidth />
        </label>
      </div>

      {!canEdit ? (
        <p className="rounded-lg bg-base-200 px-3 py-2 text-xs text-[var(--color-fg-muted)]">
          {t('dispatchPolicies.readOnly')}
        </p>
      ) : null}

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner size="md" />
        </div>
      ) : (
        <div className="space-y-2">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handlePolicyDragEnd}>
            <SortableContext items={orderedPolicies.map((policy) => policy.id)} strategy={verticalListSortingStrategy}>
              <ul className="space-y-2">
                {orderedPolicies.map((policy) => {
                  const isEditing = editingPolicyId === policy.id;
                  return (
                    <SortablePolicyItem
                      key={policy.id}
                      policy={policy}
                      isDisabled={!canEdit || isEditing || reorderPolicies.isPending}
                      dragLabel={t('dispatchPolicies.drag')}
                    >
                      {isEditing ? (
                        <div className="space-y-3">
                          {policy.systemKey && !policy.isSystemModified ? (
                            <p className="rounded-lg bg-base-200 px-3 py-2 text-xs text-[var(--color-fg-muted)]">
                              {t('dispatchPolicies.systemEditHint')}
                            </p>
                          ) : null}
                          <input
                            className="input input-bordered input-sm w-full"
                            value={editingName}
                            onChange={(event) => setEditingName(event.target.value)}
                            maxLength={100}
                          />
                          <div className="rounded-lg border border-base-300">
                            <TiptapEditor value={editingPrompt} onChangeAction={setEditingPrompt} compact hideToolbar />
                          </div>
                          <label className="flex cursor-pointer items-center gap-2 text-xs text-[var(--color-fg-muted)]">
                            <input
                              className="checkbox checkbox-xs"
                              type="checkbox"
                              checked={editingPlanMode}
                              onChange={(event) => setEditingPlanMode(event.target.checked)}
                            />
                            {t('dispatchPolicies.planMode')}
                          </label>
                          <UserConventionSelector
                            label={t('dispatchPolicies.userConventions')}
                            emptyHint={t('dispatchPolicies.noConventions')}
                            conventions={conventionOptions}
                            selectedIds={editingUserConventionIds}
                            onToggle={(id, checked) => toggleConvention(id, checked, true)}
                          />
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm"
                              onClick={() => setEditingPolicyId(null)}
                            >
                              {tCommon('actions.cancel')}
                            </button>
                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              disabled={!editingName.trim() || updatePreset.isPending}
                              onClick={() => void saveEditing()}
                            >
                              {updatePreset.isPending ? <Spinner /> : tCommon('actions.save')}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex min-w-0 items-center gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="flex min-w-0 items-center gap-1.5 text-sm font-medium">
                              <span className="truncate">{policy.promptPreset.name}</span>
                              {policy.systemKey ? (
                                <span className="badge badge-ghost badge-xs shrink-0 text-[var(--color-fg-muted)]">
                                  {policy.isSystemModified
                                    ? t('dispatchPolicies.systemModifiedBadge')
                                    : t('dispatchPolicies.systemBadge')}
                                </span>
                              ) : null}
                            </p>
                            <p className="mt-0.5 text-xs text-[var(--color-fg-subtle)]">
                              {policy.promptPreset.planMode
                                ? t('dispatchPolicies.planModeEnabled')
                                : t('dispatchPolicies.planModeDisabled')}{' '}
                              ·{' '}
                              {t('dispatchPolicies.conventionCount', {
                                count: policy.promptPreset.userConventionIds.length,
                              })}
                            </p>
                          </div>
                          {canEdit ? (
                            <div className="flex shrink-0 items-center gap-1">
                              <button
                                type="button"
                                className="btn btn-ghost btn-xs"
                                aria-label={t('dispatchPolicies.edit')}
                                onClick={() => startEditing(policy.id)}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                              <button
                                type="button"
                                className="btn btn-ghost btn-xs text-error"
                                aria-label={
                                  policy.systemKey ? t('dispatchPolicies.disableConfirm') : tCommon('actions.delete')
                                }
                                onClick={() => {
                                  if (policy.systemKey) {
                                    setDisablingPolicy(policy);
                                    return;
                                  }
                                  void deletePolicy.mutateAsync(policy.id);
                                }}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          ) : null}
                        </div>
                      )}
                    </SortablePolicyItem>
                  );
                })}
              </ul>
            </SortableContext>
          </DndContext>
          {currentPolicies.length === 0 ? (
            <p className="rounded-xl border border-dashed border-base-300 px-3 py-5 text-center text-xs text-[var(--color-fg-muted)]">
              {t('dispatchPolicies.empty')}
            </p>
          ) : null}
        </div>
      )}

      {canEdit && !isCreateOpen ? (
        <button type="button" className="btn btn-outline btn-sm w-full" onClick={() => setIsCreateOpen(true)}>
          <Plus className="h-4 w-4" />
          {t('dispatchPolicies.add')}
        </button>
      ) : null}
      {canEdit && isCreateOpen ? (
        <div className="space-y-3 rounded-xl border border-primary/30 bg-base-100 p-3">
          <input
            className="input input-bordered input-sm w-full"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={t('dispatchPolicies.presetName')}
            maxLength={100}
          />
          <div className="rounded-lg border border-base-300">
            <TiptapEditor value={prompt} onChangeAction={setPrompt} compact hideToolbar />
          </div>
          <label className="flex cursor-pointer items-center gap-2 text-xs text-[var(--color-fg-muted)]">
            <input
              className="checkbox checkbox-xs"
              type="checkbox"
              checked={planMode}
              onChange={(event) => setPlanMode(event.target.checked)}
            />
            {t('dispatchPolicies.planMode')}
          </label>
          <UserConventionSelector
            label={t('dispatchPolicies.userConventions')}
            emptyHint={t('dispatchPolicies.noConventions')}
            conventions={conventionOptions}
            selectedIds={userConventionIds}
            onToggle={toggleConvention}
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => {
                resetCreateForm();
                setIsCreateOpen(false);
              }}
            >
              {tCommon('actions.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={!name.trim() || createPolicy.isPending || createPreset.isPending}
              onClick={() => void handleCreate()}
            >
              {createPolicy.isPending || createPreset.isPending ? <Spinner /> : tCommon('actions.save')}
            </button>
          </div>
        </div>
      ) : null}

      <DeleteConfirmModal
        isOpen={disablingPolicy !== null}
        title={t('dispatchPolicies.disableTitle')}
        message={t('dispatchPolicies.disableMessage', { name: disablingPolicy?.promptPreset.name ?? '' })}
        confirmLabel={t('dispatchPolicies.disableConfirm')}
        cancelLabel={tCommon('actions.cancel')}
        isPending={deletePolicy.isPending}
        zIndex="overlay"
        onConfirm={() => {
          if (!disablingPolicy) return;
          void deletePolicy
            .mutateAsync(disablingPolicy.id)
            .then(() => {
              toast.success(t('dispatchPolicies.disabled'));
              setDisablingPolicy(null);
            })
            .catch(() => toast.error(t('dispatchPolicies.saveFailed')));
        }}
        onCancel={() => setDisablingPolicy(null)}
      />
    </section>
  );
}
